/**
 * The ocean surface mesh: a radial grid centred on the rig.
 *
 * Because the player's camera is fixed on the platform, the grid never needs to move or
 * snap — it is built once, centred on the origin, with ring spacing that grows
 * geometrically outward. That puts vertex density exactly where the camera looks down at
 * detail and thins it toward the horizon where a swell spans a pixel.
 *
 * Ring spacing is geometric (each ring `ratio` times further than the last). Compared
 * with a projected grid this is stable by construction: no re-projection jitter, no
 * back-projection edge cases at the horizon, and no per-frame vertex rebuild.
 */

import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import { BoundingInfo } from '@babylonjs/core/Culling/boundingInfo';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { Scene } from '@babylonjs/core/scene';

export interface OceanGridOptions {
  /** Radius of the innermost ring, metres. */
  innerRadius: number;
  /** Radius of the outermost ring, metres — the visual horizon. */
  outerRadius: number;
  /** Number of concentric rings. */
  rings: number;
  /** Number of angular divisions. */
  sectors: number;
}

export const OCEAN_GRID_PRESETS: Record<'ultra' | 'high' | 'medium' | 'low', OceanGridOptions> = {
  ultra: { innerRadius: 3, outerRadius: 40000, rings: 340, sectors: 256 },
  high: { innerRadius: 3, outerRadius: 40000, rings: 300, sectors: 192 },
  medium: { innerRadius: 3.5, outerRadius: 40000, rings: 216, sectors: 128 },
  low: { innerRadius: 4, outerRadius: 40000, rings: 150, sectors: 96 },
};

export interface OceanGridResult {
  mesh: Mesh;
  vertexCount: number;
  triangleCount: number;
}

export function createOceanGrid(scene: Scene, opts: OceanGridOptions): OceanGridResult {
  const { innerRadius, outerRadius, rings, sectors } = opts;

  // Geometric radii: r_i = inner * ratio^i, with ratio chosen to land exactly on outer.
  const ratio = Math.pow(outerRadius / innerRadius, 1 / (rings - 1));
  const radii = new Float64Array(rings);
  for (let i = 0; i < rings; i++) radii[i] = innerRadius * Math.pow(ratio, i);

  // Vertices: a centre point, then rings x sectors.
  const vertexCount = 1 + rings * sectors;
  const positions = new Float32Array(vertexCount * 3);
  const uvs = new Float32Array(vertexCount * 2);
  const normals = new Float32Array(vertexCount * 3);

  // Centre.
  positions[0] = 0;
  positions[1] = 0;
  positions[2] = 0;
  uvs[0] = 0;
  uvs[1] = 0;
  normals[1] = 1;

  for (let r = 0; r < rings; r++) {
    const radius = radii[r] as number;
    const ringT = r / (rings - 1);
    for (let s = 0; s < sectors; s++) {
      const idx = 1 + r * sectors + s;
      const angle = (s / sectors) * Math.PI * 2;
      positions[idx * 3 + 0] = Math.sin(angle) * radius;
      positions[idx * 3 + 1] = 0;
      positions[idx * 3 + 2] = Math.cos(angle) * radius;
      uvs[idx * 2 + 0] = ringT;
      uvs[idx * 2 + 1] = s / sectors;
      normals[idx * 3 + 0] = 0;
      normals[idx * 3 + 1] = 1;
      normals[idx * 3 + 2] = 0;
    }
  }

  // Indices: a triangle fan for the centre cap, then quad strips between rings.
  const triangleCount = sectors + (rings - 1) * sectors * 2;
  const use32 = vertexCount > 65535;
  const indices = use32 ? new Uint32Array(triangleCount * 3) : new Uint16Array(triangleCount * 3);
  let t = 0;

  // Centre fan (wound counter-clockwise when viewed from above, so normals face up).
  for (let s = 0; s < sectors; s++) {
    const a = 1 + s;
    const b = 1 + ((s + 1) % sectors);
    indices[t++] = 0;
    indices[t++] = b;
    indices[t++] = a;
  }

  // Ring strips.
  for (let r = 0; r < rings - 1; r++) {
    const base = 1 + r * sectors;
    const next = 1 + (r + 1) * sectors;
    for (let s = 0; s < sectors; s++) {
      const s1 = (s + 1) % sectors;
      const i0 = base + s;
      const i1 = base + s1;
      const i2 = next + s;
      const i3 = next + s1;
      indices[t++] = i0;
      indices[t++] = i3;
      indices[t++] = i2;
      indices[t++] = i0;
      indices[t++] = i1;
      indices[t++] = i3;
    }
  }

  const mesh = new Mesh('ocean', scene);
  const data = new VertexData();
  data.positions = positions as unknown as number[];
  data.indices = indices as unknown as number[];
  data.uvs = uvs as unknown as number[];
  data.normals = normals as unknown as number[];
  data.applyToMesh(mesh, false);

  // The vertex shader displaces well outside the flat disc's bounds, and the mesh is
  // always in view anyway — give it a generous box so it is never wrongly culled.
  const extent = outerRadius * 1.2;
  mesh.setBoundingInfo(
    new BoundingInfo(new Vector3(-extent, -600, -extent), new Vector3(extent, 600, extent)),
  );
  mesh.alwaysSelectAsActiveMesh = true;
  mesh.isPickable = false;
  // Water is drawn first and fills the frame; nothing behind it needs to be shaded.
  mesh.renderingGroupId = 0;

  return { mesh, vertexCount, triangleCount };
}
